import React, { useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import ListViewV3 from "../../components/ListViewV3/ListViewV3";
import userContext from "../../context/users/UserContext";
import { Dropdown, PrimaryButton, TextField, DatePicker, defaultDatePickerStrings } from "@fluentui/react";
import loaderContext from "../../context/loader/LoaderContext";
import toastContext from "../../context/toast/ToastContext";
import ColumnsFirm2Firm from "./ColumnsFirm2Firm";
import {
  firm2FirmFilterInitialState,
  sendingStatusOptions,
} from "./Helper";
import { getFirm2FirmList, firm2FirmExportCsvFetchProgress, firm2FirmExportCsvDownloadFile } from "../../service/Firm2FirmService";
import Moment from "moment";
import CustomMultiSelect from "../../components/CustomMultiSelect";
import { ACTIONS, AuthRoutes, SELECTED_TAB_KEY, TOAST_TYPE, USERTYPES } from "../../constants/Constants";
import Firm2FirmExportCsvModal from "./components/ExportCsv/Firm2FirmExportCsvModal";
import { fi } from "date-fns/locale";

const PAGE_SIZE = 20;

const Firm2FirmList = () => {
  const [t] = useTranslation();
  const { loadByUsername, authenticated , roles} = useContext(userContext);
  const [dataQueryState, setDataQueryState] = useState({});
  const [displayListAppliedFilter, setDisplayListAppliedFilter] = useState([]);
  const [firm2FirmFilterObj, setFirm2FirmFilterObj] = useState(
    firm2FirmFilterInitialState
  );
  const { setLoaderRunning, ref } = useContext(loaderContext);
  const [paginationObj, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [filterObj, setFilterObj] = useState({
    filterInput: null,
    filterUpdate: 0,
  });
  const [isInitialCall, setIsInitialCall] = useState(true);
  const [clientOptions, setClientOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [showFirm2FirmExportCsvModal, setShowFirm2FirmExportCsvModal] = useState(false);
  const [firm2FirmExportProgress, setFirm2FirmExportProgress] = useState(0);
  const [firm2FirmExportProgressId, setFirm2FirmExportProgressId] = useState(0);
  const [firm2FirmExportToken, setFirm2FirmExportToken] = useState("");
  const [firm2FirmExportFetchInterval] = useState(2000);
  const firm2FirmExportPollIntervalRef = useRef(null);

  useEffect(() => {
    if (!authenticated) {
      loadByUsername();
    }
  }, []);

  useEffect(() => {
    if (isInitialCall) {
      setIsInitialCall(false);
    } else {
      setFirm2FirmFilterObj(firm2FirmFilterInitialState);
      setDisplayListAppliedFilter([]);
      setPagination(() => {
        const newItem = { ...paginationObj };
        newItem.pageIndex = 0;
        return newItem;
      });

      setTimeout(() => {
        setFilterObj(() => {
          const newItem = { ...filterObj };
          newItem.filterInput = null;
          newItem.filterUpdate = newItem.filterUpdate + 1;
          return newItem;
        });
      }, 100);
    }
  }, []);

  const fetchFirm2FirmList = async (options) => {
    const body = {
      paginationDto: {
        pageNumber: options.page,
        pageSize: PAGE_SIZE,
        filterInput:
          options.filterObj.filterInput == null ||
          options.filterObj.filterInput == ""
            ? null
            : options.filterObj.filterInput,
        destination:
          firm2FirmFilterObj.destination?.length > 0
            ? firm2FirmFilterObj.destination
            : null,
        patientId:
          firm2FirmFilterObj.patientId == null ||
          firm2FirmFilterObj.patientId == ""
            ? null
            : firm2FirmFilterObj.patientId,
        patientName:
          firm2FirmFilterObj.patientName == null ||
          firm2FirmFilterObj.patientName == ""
            ? null
            : firm2FirmFilterObj.patientName,
        jobId:
          firm2FirmFilterObj.jobId == null
            ? null
            : firm2FirmFilterObj.jobId,
        sendingStatus:
          firm2FirmFilterObj.sendingStatus == null
            ? null
            : firm2FirmFilterObj.sendingStatus,
        clientNames:
          firm2FirmFilterObj.clientNames?.length > 0
            ? firm2FirmFilterObj.clientNames
            : null,
          createdOnFrom: firm2FirmFilterObj.createdOnFrom || null,
          createdOnTo: firm2FirmFilterObj.createdOnTo || null,
      },
    };

    setLoaderRunning(true);
    ref.current.continuousStart();

    try {
      const response = await getFirm2FirmList(body);
      let data = null;
      let pageCount = null;
      if (response.status === 200 && response.data.result === "SUCCESS") {
        setLoaderRunning(false);
        ref.current.complete();
        data = response.data.data.results.content;
        pageCount = response.data.data.results.totalPages;
        const responseData = response.data.data;
         if (responseData.clients) {
          const seen = new Set();
          const uniqueOptions = responseData.clients
            .map(c => ({ key: c.name, text: c.name }))
            .filter(o => seen.has(o.key) ? false : seen.add(o.key));
          setClientOptions(uniqueOptions);
        }
        if (responseData.destinations) {
          const seen = new Set();
          const uniqueOptions = responseData.destinations
            .map(d => ({ key: d.name, text: d.name }))
            .filter(o => seen.has(o.key) ? false : seen.add(o.key));
          setDestinationOptions(uniqueOptions);
        }
        return {
          rows: data,
          pageCount: pageCount,
        };
      } else {
        setLoaderRunning(false);
        ref.current.complete();
        return {
          rows: data,
          pageCount: pageCount,
        };
      }
    } catch (e) {
      console.log(e);
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  function ReloadList() {
    setFilterObj((prevFilterObj) => ({
      ...prevFilterObj,
      filterUpdate: prevFilterObj.filterUpdate + 1,
    }));
  }

  const getOptionText = (options, key) => {
    const found = options.find((o) => o.key === key);
    return found ? found.text : key;
  };

  const { showToastv2, updateProgress, hideProgress } = useContext(toastContext);

  const handleExportInitiated = (progressId, token) => {
    setFirm2FirmExportProgressId(progressId);
    setFirm2FirmExportToken(token);
    setFirm2FirmExportProgress(1);
  };

  const getFileName = (response) => {
    var disposition = response.headers["content-disposition"];
    var filename = "firm_export.csv";
    if (disposition && disposition.indexOf("attachment") !== -1) {
      var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      var matches = filenameRegex.exec(disposition);
      if (matches != null && matches[1]) {
        filename = matches[1].replace(/['"]/g, "");
      }
    }
    return filename;
  };

  const downloadFirm2FirmExportFile = async (token) => {
    const response = await firm2FirmExportCsvDownloadFile(token);
    if (!response) {
      showToastv2(TOAST_TYPE.ERROR, "Error while downloading report.");
      return;
    }
    if (response.status === 200) {
      const file = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getFileName(response));
      document.body.appendChild(link);
      link.click();
      showToastv2(TOAST_TYPE.SUCCESS, "Export CSV downloaded");
      setFirm2FirmExportProgress(101);
      link.remove();
    } else {
      showToastv2(TOAST_TYPE.ERROR, "Error while downloading report.");
    }
  };

  const fetchFirm2FirmExportProgress = async (interval, token) => {
    const response = await firm2FirmExportCsvFetchProgress(token);
    if (!response || !response.data || !response.data.data) {
      clearInterval(interval);
      showToastv2(TOAST_TYPE.ERROR, "Failed to fetch export progress.");
      return;
    }
    if (response.status === 200) {
      if (response.data.data.status === "FAILED") {
        hideProgress(firm2FirmExportProgressId);
        setFirm2FirmExportProgress(0);
        showToastv2(
          TOAST_TYPE.ERROR,
          response.data.data.error
            ? response.data.data.error
            : "Failed to download file."
        );
        clearInterval(interval);
        return;
      }
      setFirm2FirmExportProgress(response.data.data.progress);
    }
    return response.data.data.progress;
  };

  useEffect(() => {
    if (firm2FirmExportProgress === 0) return;

    if (firm2FirmExportProgress < 100) {
      updateProgress(firm2FirmExportProgressId, firm2FirmExportProgress);
      const oldProgress = firm2FirmExportProgress;
      const currentToken = firm2FirmExportToken;
      let progress;
      const interval = setInterval(async () => {
        progress = await fetchFirm2FirmExportProgress(interval, currentToken);
        if (progress > oldProgress) {
          clearInterval(interval);
        }
      }, firm2FirmExportFetchInterval);
      firm2FirmExportPollIntervalRef.current = interval;
    }

    if (firm2FirmExportProgress === 100) {
      updateProgress(firm2FirmExportProgressId, firm2FirmExportProgress);
      downloadFirm2FirmExportFile(firm2FirmExportToken);
    }

    if (firm2FirmExportProgress === 101) {
      updateProgress(firm2FirmExportProgressId, 100);
      setTimeout(() => {
        hideProgress(firm2FirmExportProgressId);
        setFirm2FirmExportProgress(0);
      }, 3000);
    }

    return () => {
      if (firm2FirmExportPollIntervalRef.current) {
        clearInterval(firm2FirmExportPollIntervalRef.current);
      }
    };
  }, [firm2FirmExportProgress, firm2FirmExportToken]);

   const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const formatBadgeDate = (ddmmyyyy, prefix) => {
    if (!ddmmyyyy) return null;
    const [d, m, y] = ddmmyyyy.split('/');
    return `${prefix}: ${d}-${monthNames[parseInt(m, 10) - 1]}-${y}`;
  };

  const handleApplyClick = (filterValue) => {
    if (filterValue != undefined) filterValue = filterValue.trim();
    const transformedArray = Object.keys(firm2FirmFilterObj)
      .map((key) => {
        let val = firm2FirmFilterObj[key];
        if (key === "sendingStatus") {
          val = getOptionText(sendingStatusOptions, val);
        }
        if (key === "clientNames" && Array.isArray(val)) {
            return val.map(clientName => ({ key, label: clientName }));
          }
        if (key === "destination" && Array.isArray(val)) {
            return val.map(dest => ({ key, label: dest }));
          }
        if (key === "createdOnFrom") {
          val = formatBadgeDate(val, "From");
        }
        if (key === "createdOnTo") {
          val = formatBadgeDate(val, "To");
        }
        return { key, label: val };
      })
      .flat().filter((item) => item.label !== null && item.label !== "");

    setDisplayListAppliedFilter(transformedArray);

    setPagination(() => {
      const newItem = { ...paginationObj };
      newItem.pageIndex = 0;
      return newItem;
    });
    setTimeout(() => {
      setFilterObj(() => {
        const newItem = { ...filterObj };
        if (filterValue != null) {
          newItem.filterInput = filterValue;
        }
        newItem.filterUpdate = newItem.filterUpdate + 1;
        return newItem;
      });
    }, 100);
  };

  const filterList = () => {};

  const handleClearFilter = () => {
    setFirm2FirmFilterObj(firm2FirmFilterInitialState);
    setDisplayListAppliedFilter([]);
    setPagination(() => {
      const newItem = { ...paginationObj };
      newItem.pageIndex = 0;
      return newItem;
    });

    setTimeout(() => {
      setFilterObj(() => {
        const newItem = { ...filterObj };
        newItem.filterInput = null;
        newItem.filterUpdate = newItem.filterUpdate + 1;
        return newItem;
      });
    }, 100);
  };

  const removeBadgeFromFilter = (toBeRemoveObj) => {
    setFirm2FirmFilterObj((currentFilterObj) => {
      const updatedFilterObj = { ...currentFilterObj };

      if (updatedFilterObj.hasOwnProperty(toBeRemoveObj.key)) {
        const currentValue = currentFilterObj[toBeRemoveObj.key];
        updatedFilterObj[toBeRemoveObj.key] = Array.isArray(currentValue)
          ? currentValue.filter((item) => item !== toBeRemoveObj.label)
          : null;
      }

      return updatedFilterObj;
    });

    setDisplayListAppliedFilter((current) =>
      current.filter((item) => item.label !== toBeRemoveObj.label)
    );

    setPagination(() => {
      const newItem = { ...paginationObj };
      newItem.pageIndex = 0;
      return newItem;
    });

    setTimeout(() => {
      setFilterObj(() => {
        const newItem = { ...filterObj };
        newItem.filterUpdate = newItem.filterUpdate + 1;
        return newItem;
      });
    }, 100);
  };

  const columns = ColumnsFirm2Firm();

  const buttonsTopBar = [
    ...((roles?.[0] == USERTYPES.ADMIN ||
      ((roles?.[0] == USERTYPES.FIRMADMIN ||
        roles?.[0] == USERTYPES.SUPERVISOR )))
      ? [{
          key: "export",
          button: (
            <PrimaryButton style={{ marginRight: "10px" }}
              text="Export CSV"
              className={"common-default-active-button list-view-top-buttons-styles"}
              onClick={() => setShowFirm2FirmExportCsvModal(true)}
            />
          ),
        }]
      : []),

    {
      key: "1",
      button: (
        <PrimaryButton
          text={t("button.refresh")}
          className={"common-default-active-button list-view-top-buttons-styles"}
          onClick={ReloadList}
        />
      ),
    },
  ];

  return (
    <>
      <Firm2FirmExportCsvModal
        show={showFirm2FirmExportCsvModal}
        onClose={() => setShowFirm2FirmExportCsvModal(false)}
        firm2FirmFilterObj={firm2FirmFilterObj}
        filterInput={filterObj.filterInput}
        onExportInitiated={handleExportInitiated}
      />
      <ListViewV3
      buttonsTopBar={buttonsTopBar}
      sortDropdownOptions={[]}
      fetchData={fetchFirm2FirmList}
      showSideBySide={false}
      columns={columns}
      handleUserClick={() => {}}
      filterObj={filterObj}
      setFilterObj={setFilterObj}
      setPagination={setPagination}
      handleApplyClick={handleApplyClick}
      paginationObj={paginationObj}
      unqiueKey={"Firm2FirmDashboard"}
      placeholder={t("placeholder.order-id-search")}
      tabHeaderPresent={true}
      pointer={false}
      fullLength={true}
      setDataQueryState={setDataQueryState}
      dataQueryState={dataQueryState}
      enhancedFilter={true}
      showSort={false}
      displayListAppliedFilter={displayListAppliedFilter}
      filterList={filterList}
      handleClearFilter={handleClearFilter}
      removeBadgeFromFilter={removeBadgeFromFilter}
      filterPopUpComponent={
        <FilterPopUpComponent
          firm2FirmFilterObj={firm2FirmFilterObj}
          setFirm2FirmFilterObj={setFirm2FirmFilterObj}
          clientOptions={clientOptions}
          destinationOptions={destinationOptions}
        />
      }
    />
    </>
  );
};

export default Firm2FirmList;

const FilterPopUpComponent = ({ firm2FirmFilterObj, setFirm2FirmFilterObj, clientOptions, destinationOptions }) => {
  return (
    <div style={{ display: "flex", marginBottom: "10px" }}>
      <div
        className="study-start-date-filter-section align-filter-section"
        style={{ width: "15%" }}
      >
        <span className="filter-section-text">Patient ID</span>
        <TextField
          name="Patient Id"
          placeholder={t("placeholder.search")}
          value={firm2FirmFilterObj.patientId}
          onChange={(ev) => {
            setFirm2FirmFilterObj({
              ...firm2FirmFilterObj,
              patientId: ev.target.value,
            });
          }}
          styles={{ backgroundColor: "#2e3033" }}
        />
        <span className="filter-section-text" style={{ marginTop: "5px" }}>
          Patient Name
        </span>
        <TextField
          name="Patient Name"
          placeholder={t("placeholder.search")}
          value={firm2FirmFilterObj.patientName}
          onChange={(ev) => {
            setFirm2FirmFilterObj({
              ...firm2FirmFilterObj,
              patientName: ev.target.value,
            });
          }}
          styles={{
            fieldGroup: {
              height: "32px",
              display: "block",
            },
            field: {
              paddingTop: "6px",
            },
          }}
        />
      </div>

      <div className="filter-section-seperator"></div>

      <div
        className="study-start-date-filter-section align-filter-section"
        style={{ width: "15%" }}
      >
        <span className="filter-section-text">Destination</span>
        <CustomMultiSelect        
          options={destinationOptions}
          placeholder="Select destination"
          searchPlaceholder="Search destinations"
          selectedKeys={firm2FirmFilterObj.destination || []}
          setSelectedKeys={(keys) => {
            setFirm2FirmFilterObj({
              ...firm2FirmFilterObj,
              destination: keys || [],
            });
          }}
          calloutProps={{ doNotLayer: true }}
          showSelectAll={true}
          styles={{
            root: { marginTop: 0 },
            title: { marginBottom: 0 },
          }}
        />
        <span className="filter-section-text" style={{ marginTop: "5px" }}>
          Job Id
        </span>
        <TextField
          name="Job Id"
          placeholder={t("placeholder.search")}
          value={firm2FirmFilterObj.jobId}
          onChange={(ev) => {
            setFirm2FirmFilterObj({
              ...firm2FirmFilterObj,
              jobId: ev.target.value,
            });
          }}
          styles={{ backgroundColor: "#2e3033" }}
        />
      </div>

      <div className="filter-section-seperator"></div>

      <div
        className="study-start-date-filter-section align-filter-section"
        style={{ width: "15%" }}
      >
        <span className="filter-section-text">Request Created From</span>
        <DatePicker
         formatDate={(date) => {
              const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              return `${String(date.getDate()).padStart(2, '0')}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
            }}
          placeholder="Select date"
          value={firm2FirmFilterObj.createdOnFrom
                    ? (() => {
                        const [d, m, y] = firm2FirmFilterObj.createdOnFrom.split('/');
                        return new Date(y, m - 1, d);
                      })()
                    : null}
          onSelectDate={(date) => {
              const formatted = date
                ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
                : null;
              setFirm2FirmFilterObj({
                ...firm2FirmFilterObj,
                createdOnFrom: formatted,
              });
            }}
          strings={defaultDatePickerStrings}
          styles={{ root: { marginTop: 0 } }}
          calloutProps={{ doNotLayer: true }}
        />
        <span className="filter-section-text" style={{ marginTop: "5px" }}>
          Request Created To
        </span>
        <DatePicker
         formatDate={(date) => {
              const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              return `${String(date.getDate()).padStart(2, '0')}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
            }}
          placeholder="Select date"
          value={firm2FirmFilterObj.createdOnTo
                    ? (() => {
                        const [d, m, y] = firm2FirmFilterObj.createdOnTo.split('/');
                        return new Date(y, m - 1, d);
                      })()
                    : null}
          onSelectDate={(date) => {
              const formatted = date
                ? `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`
                : null;
              setFirm2FirmFilterObj({
                ...firm2FirmFilterObj,
                createdOnTo: formatted,
              });
            }}
          strings={defaultDatePickerStrings}
          styles={{ root: { marginTop: 0 } }}
          calloutProps={{ doNotLayer: true }}
        />
      </div>

      <div className="filter-section-seperator"></div>

      <div
        className="study-start-date-filter-section align-filter-section"
        style={{ width: "15%" }}
      >
        <span className="filter-section-text">Sending Status</span>
        <Dropdown
          placeholder="Select status"
          options={sendingStatusOptions}
          selectedKey={firm2FirmFilterObj.sendingStatus}
          onChange={(event, option) => {
            setFirm2FirmFilterObj({
              ...firm2FirmFilterObj,
              sendingStatus: option.key,
            });
          }}
          calloutProps={{ doNotLayer: true }}
          styles={{
            root: { marginTop: 0, marginBottom: "6px" },
          }}
        />
        <span className="filter-section-text" >
          Client
        </span>
        <CustomMultiSelect
          options={clientOptions}
          placeholder="Select clients"
          searchPlaceholder="Search clients"
          selectedKeys={firm2FirmFilterObj.clientNames || []}
          setSelectedKeys={(keys) => {
            setFirm2FirmFilterObj({
              ...firm2FirmFilterObj,
              clientNames: keys || [],
            });
          }}
          showSelectAll={true}
        />
      </div>
      
    </div>
  );
};
